import { setBrowserStorageItem, clearBrowserStorage, updateSavedSteps, updateSavedLoanCalculations, endPresentation } from '@utils/browserStorage';
import { useViewport } from '@hooks/useViewport';
import { BROWSER_STORAGE_KEYS, ANALYTICS_EVENTS, SHARED_STYLES, QUERY_KEYS, PING_INTERVALS, PresentationType } from '@constants';
import { pingConnection } from '@utils/pingConnection';
import { updateAnalytics, updateSlideAnalytics, updateConnection, updateCache, updatePresentationStart, updatePresentationEnd } from '@services';
import { 
    setSlideWidth,
    setClientConnected,
    setPresentationType,
    setRepConnected,
    setLanguage,
    setSlideId,
    setPdfPage,
    selectTerm,
    setShowPointer,
    updateVideoData,
    updatePointerPosition,
    selectInfoItem,
    setStepsReviewed,
    setLoanInput,
    setScrollTop,
    setScrollLeft,
    setPresentationEnded,
    setPresentationId,
    setLastSlideId
} from '@actions/creators';
import { createAnalyticsEventBody } from './analytics';

/* General handlers */ 
export const parseStreamData = (event) => {
    try {
        return JSON.parse(event.data) ?? null;
    } catch (error) {
        console.error( `Error parsing event data ${JSON.stringify(event.data)} to JSON`);
    }
};

export const errorHandler = (error) => {
    console.error('An error occurred in the event stream', error);
};

export const logOpenMessage = (type = 'client') => () => {
    const message = 'Connected to the event stream';
    const isClient = type === 'client';

    const logMessage = isClient ? message + '!' : message + '. Waiting for client to connect...';

    console.log(logMessage);
};

/* Rep-specific handlers */ 

export function pingAfterDelay(time, data, type = 'interval') {
    const delayFunc = (data) => async () => { 
        await pingConnection(data);
    };

    const isInterval = type === 'interval';

    return isInterval ? setInterval(delayFunc(data), time) : setTimeout(delayFunc(data), time);
}

export function getPingData(locationState, presentationData) {
    const { leadId: repLeadId, profile, presentationId } = locationState;

    const data = { 
        repLeadId, 
        profile, 
        presentationId, 
        repConnected: true, 
        ...presentationData };

    return data;
}

export function checkLocationState(locationState) {
    if (locationState) {
       return true;
    } else {
        console.warn('Location state not found; you should have been prompted to login');
        return false;
    }
}

/* Send an initial connection message with a slight delay when page loads.
    Sometimes the deployed instances in Azure are fast that the (re)connection message beats 
    the disconnection message (which happens in the backend whenever the event stream closes) 
    on reload, causing inconsistencies in connection status. */
export const repOnOpenHandler = (locationState, presentationData, delayTime) => () => {
    logOpenMessage('rep')();

    if (checkLocationState(locationState)) {
        const data = getPingData(locationState, presentationData);

        pingAfterDelay(delayTime, data, 'timeout');
    }          
};

/* Client handlers */

function updateStateAndBrowserStorage(dispatch, action, value, storageKey) {
    dispatch(action(value));
    setBrowserStorageItem(storageKey, value);
}

export const repConnectionHandler = ({ dispatch, slideRef, setShowViewportButton, analyticsMutation, isSlidesPresentation, locationState }) => async (event) => {
    const data = parseStreamData(event);
    const { clientConnected, repConnected, dimensions } = data;
    const { clientConnectionKey } = BROWSER_STORAGE_KEYS;

    /* Will update this in the future - discovered that the current implementation was working by setting
       clientConnected to undefined when rep disconnected. We should be getting 'clientDisconnected' message - will make sure this is being sent in the future.
       Keep the current implementation for now but without setting clientConnected to undefined. */
    if (repConnected === false) {
        updateStateAndBrowserStorage(dispatch, setClientConnected, false, clientConnectionKey)
    }

    // Dimensions will not be included if this is a disconnection message. 
    // Update connection status regardless.
    if (data.hasOwnProperty('clientConnected')) {
        updateStateAndBrowserStorage(dispatch, setClientConnected, clientConnected, clientConnectionKey)
    }
    
    // Resize the slide and update the styles based on dimensions received from the client. 
    // Dimensions are only sent with connection events, not disconnection events
    if (dimensions) {
        resizeCurrentSlide(dimensions, dispatch, slideRef, setShowViewportButton);
        dispatch(setPresentationEnded(false));

        if (clientConnected) {
            await dispatchPresentationStartedEvent(analyticsMutation, isSlidesPresentation, locationState);
        }
    }
};

async function dispatchPresentationStartedEvent(analyticsMutation, isSlidesPresentation, locationState) {
    const { slidesPresentationStarted, docsPresentationStarted } = ANALYTICS_EVENTS;
    const presentationStartedEvent = isSlidesPresentation ? slidesPresentationStarted : docsPresentationStarted;
    const presentationId = locationState ? locationState.presentationId : null;

    analyticsMutation.mutate(createAnalyticsEventBody(presentationId, presentationStartedEvent));

    // To-do: Only do this once
    if (presentationId) {
        const presentationStart = new Date();
        await updatePresentationStart({ presentationId, presentationStart });
    }
}

function resizeCurrentSlide(dimensions, dispatch, slideRef, setShowViewportButton) {
    const { height, width } = dimensions;
    const { maxSlideWidth } = SHARED_STYLES;
    const { slideDimensionsKey } = BROWSER_STORAGE_KEYS;
    const maxSlideWidthAsNumber = parseInt(maxSlideWidth);

    if (width && width <= maxSlideWidthAsNumber) {
        console.log(`Connection to client confirmed! Resizing viewport to width ${width}`);
    
        dispatch(setSlideWidth(width));
        useViewport(width, dispatch);

        width < maxSlideWidthAsNumber ? setShowViewportButton(true) : setShowViewportButton(false);
        
        if (slideRef.current !== null) {
            slideRef.current.style.height = `${height}px`;
            slideRef.current.style.width = `${width}px`;
        }

        setBrowserStorageItem(slideDimensionsKey, { height, width }); 
    }
}

/* Client-specific handlers */

export const clientConnectionHandler = event => {
    const data = parseStreamData(event);

    if (data.hasOwnProperty('clientConnected')) {
        const { clientConnected, dimensions } = data;

        if (clientConnected) {
            console.log(`Notified financial consultant of connection with dimensions ${JSON.stringify(dimensions)}`);
        } else {
            console.log('Notified financial consultant of disconnection');
        }
    } else if (data.hasOwnProperty('repConnected')) {
        console.info(`Consultant connection status: ${data.repConnected}`);
    }
};

export const startPresentationHandler = (dispatch, sseState, queryClient, setRefetchInterval) => event => {
    const { presentationType, repConnected, slideId, presentationId, firstMount } = parseStreamData(event);
    const { presentationTypeKey, presentationEndedKey, slideIdKey } = BROWSER_STORAGE_KEYS;
    const { clientQuery } = QUERY_KEYS;
    const { threeSeconds } = PING_INTERVALS;

    /* The START event sets repConnected to true, which triggers the first slide to load.
        When the first slide loads, it gives the slideRef a node to attach itself to.
        This triggers the resize observer to send a CONNECTION event - 
        Notifying the rep of client connection (with viewport dimensions included)
    */

    dispatch(setPresentationId(presentationId));
    updateStateAndBrowserStorage(dispatch, setPresentationType, presentationType, presentationTypeKey);
    dispatch(setRepConnected(repConnected));
    setBrowserStorageItem(presentationEndedKey, false, sessionStorage);

    if (slideId && slideId !== sseState.slideId) {
        updateStateAndBrowserStorage(dispatch, setSlideId, slideId, slideIdKey);
    }

    if (firstMount) {
        queryClient.invalidateQueries(clientQuery);
        setRefetchInterval(threeSeconds);
    }

    // Trying different approaches of refetching vs query invalidation
    // queryClient.invalidateQueries(clientQuery);
};

export const languageHandler = dispatch => event => {
    const { language } = parseStreamData(event);

    dispatch(setLanguage(language));
};

export const slideIdHandler = dispatch => event => {
    const { slideId, lastSlideId, presentationId } = parseStreamData(event);
    const { slideIdKey, presentationIdKey, lastSlideIdKey } = BROWSER_STORAGE_KEYS;
    const slideIdAsNumber = parseInt(slideId);
    const lastSlideIdAsNumber = parseInt(lastSlideId)

    updateStateAndBrowserStorage(dispatch, setSlideId, slideIdAsNumber, slideIdKey);
    updateStateAndBrowserStorage(dispatch, setLastSlideId, lastSlideIdAsNumber, lastSlideIdKey);
    updateStateAndBrowserStorage(dispatch, setPresentationId, presentationId, presentationIdKey);
};

export const pdfPageHandler = dispatch => event => {
    const { pageNumber } = parseStreamData(event);
    const { pdfPageKey } = BROWSER_STORAGE_KEYS;

    updateStateAndBrowserStorage(dispatch, setPdfPage, pageNumber, pdfPageKey);
};

export const presentationTypeHandler = dispatch => event => {
    const { presentationType } = parseStreamData(event);
    const { presentationTypeKey } = BROWSER_STORAGE_KEYS;

    updateStateAndBrowserStorage(dispatch, setPresentationType, presentationType, presentationTypeKey);
};

export const selectTermHandler = dispatch => event => {
    const { term, slideId } = parseStreamData(event);
    const { selectedTermKey } = BROWSER_STORAGE_KEYS;
    
    dispatch(selectTerm(term, slideId));
    setBrowserStorageItem(`${selectedTermKey}_${slideId}`, term);
};

export const showPointerHandler = dispatch => event => {
    const { showPointer } = parseStreamData(event);
    const { pointerKey } = BROWSER_STORAGE_KEYS;
    
    updateStateAndBrowserStorage(dispatch, setShowPointer, showPointer, pointerKey);
};

export const pointerPositionHandler = dispatch => event => {
    const { pointerPosition } = parseStreamData(event);
                
    dispatch(updatePointerPosition(pointerPosition));
};

export const videoHandler = dispatch => event => {
    const { videoData } = parseStreamData(event);

    dispatch(updateVideoData(videoData));
};

export const infoItemHandler = dispatch => event => {
    const { infoItem } = parseStreamData(event);
    const { checkboxKey } = BROWSER_STORAGE_KEYS;
    
    updateStateAndBrowserStorage(dispatch, selectInfoItem, infoItem, checkboxKey);
};

export const stepReviewedHandler = dispatch => event => {
    const { slideId, step } = parseStreamData(event);

    dispatch(setStepsReviewed(slideId, step));
    updateSavedSteps(slideId, step);
};

export const loanInputHandler = dispatch => event => {
    const { name, value } = parseStreamData(event);
                
    dispatch(setLoanInput(name, value));
    updateSavedLoanCalculations(name, value);
};

export const scrollHandler = dispatch => event => {
    const { elementId, top, left } = parseStreamData(event);
    const { scrollPositionKey } = BROWSER_STORAGE_KEYS;
    const storageKey = `${scrollPositionKey}_${elementId}`;

    dispatch(setScrollTop(elementId, parseInt(top)));
    dispatch(setScrollLeft(elementId, parseInt(left)));

    setBrowserStorageItem(storageKey, { scrollTop: top, scrollLeft: left });
};

export const endPresentationHandler = (dispatch, leadId, stream) => async event => {
    const { presentationType, lastSlideId, presentationId } = parseStreamData(event);
    const { slidesPresentationEnded, docsPresentationEnded, slideEnded } = ANALYTICS_EVENTS;
    const { SLIDES_PRESENTATION } = PresentationType;
    
    // Save this value in session storage instead of local storage
    endPresentation(dispatch, sessionStorage);

    const data = { clientConnected: false };

    await updateConnection(leadId, data);
    await updateCache(leadId, data);

    const isSlidesPresentation = presentationType === SLIDES_PRESENTATION;

    if (isSlidesPresentation) {
        await updateSlideAnalytics({ metricName: slideEnded, slideId: lastSlideId, presentationId });
    }

    const endPresEvent = isSlidesPresentation ? slidesPresentationEnded : docsPresentationEnded;

    await updateAnalytics(createAnalyticsEventBody(presentationId, endPresEvent));
    await updatePresentationEnd({ presentationId, presentationEnd: new Date(Date.now()) })

    // Clear localStorage (default) - all the presentation data except presentationEnded
    clearBrowserStorage();
    stream.close();

    console.log('Connection closed');
};
